/** Shared design language for both themes. Palette-neutral values (type,
 *  spacing, radii, shadows, motion) + the Artie emerald accent. Light/dark
 *  themes consume these so the modern look stays in sync across modes. */
// NOTE: kept in sync with src/styles/theme.css and shared/brand-tokens.json.
// The landing page _brand.generated.css copies the brand/surface values
// (it can't import this file — see shared/gen-brand-tokens.mjs).
// If you change brand.primary or the dark surfaces here, update those too.
export const tokens = {
  fontFamily:
    "'Hanken Grotesk Variable', 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  /** Brand palette — graphite + emerald design system. Kept in sync with
   *  src/styles/theme.css (the deep-emerald light default) and the per-mode
   *  BRAND_ACCENT in src/theme/appearance.ts.
   *  - primary   #10805c  emerald   (actions, links, active states — light)
   *  - secondary #e6f4ee  emeraldTint (soft fills)
   *  - tertiary  #cd8e54  caramel   (accents only)                          */
  brand: {
    primary: "#10805c",
    primaryContrast: "#ffffff",
    secondary: "#e6f4ee",
    secondaryContrast: "#0b6b4d",
    tertiary: "#cd8e54",
    tertiaryContrast: "#3a2410",
    neutral: "#F9FAFB",
    primarySoft: "rgba(16,128,92,0.14)",
    tertiarySoft: "rgba(205,142,84,0.16)",
    primaryRgb: "16,128,92",
    secondaryRgb: "230,244,238",
    tertiaryRgb: "205,142,84",
  },
  // Corner-radius scale (px). Mirrors --radius-* in styles/theme.css.
  // `bubble` = the message-bubble radius. Full pills use `pill`.
  // `control` is a touch softer than `sm` — MUI's Button/IconButton/MenuItem
  // (top-header actions + hover-highlight controls) read a little more
  // rounded than `sm`-radius surfaces like Soft's small-card variant or the
  // AppShell nav rail, which stay put deliberately.
  radius: { xs: 4, sm: 6, control: 8, md: 10, lg: 14, bubble: 10, pill: 999 },
  shadow: {
    // One cool, flat elevation ramp — no warm tint, no soft-UI extrusion.
    sm: "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)",
    md: "0 4px 12px rgba(16,24,40,0.10)",
    lg: "0 14px 36px rgba(16,24,40,0.16)",
    xl: "0 22px 56px rgba(16,24,40,0.20)",
  },
  /** Elevation scale: name → shadow token. Components reference these instead
   *  of inventing per-component shadows, so depth reads consistently.
   *  0 = flush, 1 = resting card, 2 = popover/menu, 3 = dialog. */
  elevation: {
    0: "none",
    1: "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)",
    2: "0 4px 12px rgba(16,24,40,0.10)",
    3: "0 14px 36px rgba(16,24,40,0.16)",
  },
  /** Legacy `neu` keys kept so existing consumers keep resolving, but the
   *  soft-UI look is gone: `raised` is now a flat drop shadow and `inset`
   *  is a no-op (recessed inputs get a border + focus ring instead). */
  neu: {
    light: {
      raised: "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)",
      raisedSm: "0 1px 2px rgba(16,24,40,0.07)",
      inset: "none",
    },
    dark: {
      raised: "0 1px 3px rgba(0,0,0,0.5)",
      raisedSm: "0 1px 2px rgba(0,0,0,0.45)",
      inset: "none",
    },
    accentRaised: "none",
  },
  /** Floating-chrome tokens (sidebar, composer, dialogs, popovers). Flat,
   *  borderless surfaces — the earlier glass look added bright white rims/
   *  specular highlights that read as unwanted borders, so the border and
   *  highlight are now transparent in both themes (kept as keys for any
   *  legacy consumer). */
  glass: {
    light: {
      bg: "rgba(252,251,247,0.62)",
      border: "transparent",
      blur: "blur(20px) saturate(150%)",
      highlight: "none",
      shadow: "0 16px 40px rgba(60,50,40,0.16)",
    },
    dark: {
      bg: "rgba(26,28,27,0.55)",
      border: "transparent",
      blur: "blur(22px) saturate(140%)",
      highlight: "none",
      shadow: "0 18px 46px rgba(0,0,0,0.50)",
    },
  },
  /** Motion language. Durations in ms; easings as cubic-bezier arrays so they
   *  work both as CSS strings and motion/react transition `ease` values.
   *  Replaces scattered inline `180ms` / `cubic-bezier(0.34,1.56,0.64,1)`. */
  motion: {
    duration: { fast: 150, normal: 240, slow: 400 },
    easing: {
      smooth: [0.16, 1, 0.3, 1] as [number, number, number, number],
      bounce: [0.34, 1.56, 0.64, 1] as [number, number, number, number],
    },
  },
  /** Branded keyboard focus ring (emerald). Used in a `:focus-visible` rule
   *  so it never shows on mouse clicks, only keyboard nav. */
  focusRing: {
    light: `0 0 0 2px #f6f7f8, 0 0 0 4px #10805c`,
    dark: `0 0 0 2px #121316, 0 0 0 4px #3ecf8e`,
  },
  // Editorial type scale (px). Compact base + heavier headings.
  type: {
    base: 13,
    lineHeight: 1.45,
    h5: { fontWeight: 700, letterSpacing: "-0.03em" },
    subtitle: { letterSpacing: "-0.01em" },
  },
} as const;

/** CSS helper: turn a cubic-bezier tuple into a `cubic-bezier(...)` string. */
export function cssEase(e: readonly [number, number, number, number]): string {
  return `cubic-bezier(${e[0]}, ${e[1]}, ${e[2]}, ${e[3]})`;
}
